/**
 * Sticky Add to Cart Bar - shows after scrolling past the main PDP add-to-cart area.
 * Syncs availability badge, quantity, and add-to-cart button with the main product form.
 */
(function () {
  const PUB_SUB_EVENTS = window.PUB_SUB_EVENTS || {
    variantChange: 'variant-change',
    quantityUpdate: 'quantity-update',
    cartUpdate: 'cart-update',
    cartError: 'cart-error',
  };

  function subscribe(eventName, callback) {
    return typeof window.subscribe === 'function'
      ? window.subscribe(eventName, callback)
      : () => {};
  }

  function initStickyBar(bar) {
    const sectionId = bar.dataset.sectionId;
    const productFormId = bar.dataset.productFormId;
    if (!sectionId || !productFormId) return;

    const productInfo = bar.closest('product-info');
    if (!productInfo) return;

    const sentinelId = `PDP-Add-To-Cart-Sentinel-${sectionId}`;
    const sentinel = document.getElementById(sentinelId);
    const form = document.getElementById(productFormId);
    const mainQuantityInput = productInfo.querySelector(`#Quantity-${sectionId}`);
    const mainQuantityWrapper = productInfo.querySelector('quantity-input.quantity');
    const mainSubmitButton = productInfo.querySelector(`#ProductSubmitButton-${sectionId}`);

    const stickyQtyValue = bar.querySelector('[data-sticky-qty-value]');
    const stickyQtyMinus = bar.querySelector('.sticky-bar__qty-btn[name="minus"]');
    const stickyQtyPlus = bar.querySelector('.sticky-bar__qty-btn[name="plus"]');
    const stickyQuantityContainer = bar.querySelector('.sticky-add-to-cart-bar__quantity');
    const stickySubmit = bar.querySelector('[data-sticky-submit]');
    const stickySubmitText = bar.querySelector('[data-sticky-submit-text]');
    const availabilityBadge = bar.querySelector('.sticky-bar__badge[data-availability]');

    if (!sentinel || !form) return;
    if (!mainSubmitButton) return;

    function setBarVisible(visible) {
      bar.removeAttribute('hidden');
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      bar.classList.toggle('is-visible', visible);
      document.body.classList.toggle('sticky-add-to-cart-bar-visible', visible);
    }

    function syncQuantityFromMain() {
      if (mainQuantityInput && stickyQtyValue) {
        stickyQtyValue.textContent = mainQuantityInput.value || mainQuantityInput.min || '1';
      }
      updateStickyQuantityButtons();
    }

    function updateStickyQuantityButtons() {
      if (!mainQuantityInput || !stickyQtyMinus || !stickyQtyPlus) return;
      const value = parseInt(mainQuantityInput.value, 10) || 0;
      const min = parseInt(mainQuantityInput.min, 10) || 1;
      const max = mainQuantityInput.max ? parseInt(mainQuantityInput.max, 10) : null;
      stickyQtyMinus.disabled = value <= min;
      stickyQtyPlus.disabled = max !== null && value >= max;
    }

    function triggerMainQuantityButton(name) {
      if (!mainQuantityWrapper) return;
      const btn = mainQuantityWrapper.querySelector(`.quantity__button[name="${name}"]`);
      if (btn && !btn.disabled) btn.click();
    }

    function syncSubmitFromMain() {
      if (!mainSubmitButton || !stickySubmit || !stickySubmitText) return;
      const disabled = mainSubmitButton.hasAttribute('disabled') || mainSubmitButton.getAttribute('aria-disabled') === 'true';
      stickySubmit.disabled = disabled;
      stickySubmit.classList.toggle('product_sold_out', mainSubmitButton.classList.contains('product_sold_out'));
      const textSpan = mainSubmitButton.querySelector('span:not(.loading__spinner):not(.visually-hidden)');
      if (textSpan) stickySubmitText.textContent = textSpan.textContent.trim();
    }

    function syncAvailability(available) {
      if (!availabilityBadge) return;
      const isPreOrder = bar.dataset.preOrder === 'true';
      const container = bar.querySelector('.sticky-add-to-cart-bar__availability');
      if (isPreOrder) {
        container.style.display = 'none';
        return;
      }
      container.style.display = '';
      availabilityBadge.setAttribute('data-availability', available ? 'in-stock' : 'out-of-stock');
      availabilityBadge.className = 'sticky-bar__badge ' + (available ? 'in-stock' : 'out-of-stock');
      availabilityBadge.textContent = available
        ? (bar.dataset.inStock || 'In stock')
        : (bar.dataset.outOfStock || 'Out of stock');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setBarVisible(!entry.isIntersecting);
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );
    observer.observe(sentinel);

    syncQuantityFromMain();
    if (mainQuantityInput) {
      mainQuantityInput.addEventListener('change', syncQuantityFromMain);
      mainQuantityInput.addEventListener('input', syncQuantityFromMain);
    }

    const quantityUpdateUnsub = subscribe(PUB_SUB_EVENTS.quantityUpdate, syncQuantityFromMain);

    if (stickyQtyMinus) {
      stickyQtyMinus.addEventListener('click', (e) => {
        e.preventDefault();
        triggerMainQuantityButton('minus');
        setTimeout(syncQuantityFromMain, 0);
      });
    }
    if (stickyQtyPlus) {
      stickyQtyPlus.addEventListener('click', (e) => {
        e.preventDefault();
        triggerMainQuantityButton('plus');
        setTimeout(syncQuantityFromMain, 0);
      });
    }

    syncSubmitFromMain();

    const variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      const data = payload && payload.data;
      if (data && data.sectionId && String(data.sectionId) !== String(sectionId)) return;
      const available = data && data.variant ? data.variant.available : true;
      syncAvailability(available);
      if (stickyQuantityContainer) {
        stickyQuantityContainer.style.display = available ? '' : 'none';
      }
      syncQuantityFromMain();
      setTimeout(syncSubmitFromMain, 50);
    });

    if (stickySubmit && mainSubmitButton) {
      stickySubmit.addEventListener('click', (e) => {
        e.preventDefault();
        if (stickySubmit.disabled || stickySubmit.getAttribute('aria-disabled') === 'true') return;
        mainSubmitButton.click();
      });
    }

    if (form && stickySubmit) {
      form.addEventListener('submit', () => {
        stickySubmit.classList.add('loading');
        const spinner = stickySubmit.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      });
    }

    const removeStickyLoading = () => {
      if (!stickySubmit) return;
      stickySubmit.classList.remove('loading');
      const spinner = stickySubmit.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    };

    const cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, removeStickyLoading);
    const cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, removeStickyLoading);

    let loadingObserver = null;
    if (mainSubmitButton && stickySubmit) {
      loadingObserver = new MutationObserver(() => {
        if (!mainSubmitButton.classList.contains('loading')) removeStickyLoading();
      });
      loadingObserver.observe(mainSubmitButton, { attributes: true, attributeFilter: ['class'] });
    }

    bar._stickyBarCleanup = () => {
      observer.disconnect();
      loadingObserver && loadingObserver.disconnect();
      quantityUpdateUnsub && quantityUpdateUnsub();
      variantChangeUnsub && variantChangeUnsub();
      cartUpdateUnsub && cartUpdateUnsub();
      cartErrorUnsub && cartErrorUnsub();
      if (mainQuantityInput) {
        mainQuantityInput.removeEventListener('change', syncQuantityFromMain);
        mainQuantityInput.removeEventListener('input', syncQuantityFromMain);
      }
    };
  }

  function run() {
    document.querySelectorAll('.sticky-add-to-cart-bar[data-section-id]').forEach(initStickyBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', run);
})();
